/**
 * sessionTracker — opens/closes a server-side audit row in v1_2_user_sessions
 * for each OneApp login. Keeps the session UUID in AsyncStorage so /end can
 * be called even after the JS bundle restarts (cold-start logout).
 *
 * Failure mode is by design: any network/server error here is logged and
 * swallowed. We never want a logging audit to break login/logout for the user.
 * The backend's stale-sessions worker will eventually close any /start that
 * never had a matching /end (uninstall, force-quit, crash) — see
 * docs/API_DOCUMENTATION_v2.md §32.
 *
 * Stored: the open row, and the rows still to be closed. Each carries its
 * owner (the Firebase uid it was opened for). A row the server would not
 * close — the session had already expired, or the token was the client's
 * under "Login as user" — goes on the pending list with the reason it should
 * have been closed for, and is closed the next time its owner signs in; one
 * that fails again stays on the list. Rows used to be forgotten on any
 * failure, and an expired session's row was never closed at all.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { v2StartSession, v2EndSession } from './v2/api';
import {collectDeviceInfo} from './deviceInfo';

const STORAGE_KEY = 'oneapp.user_session.id';
/** The pending list is a courtesy before the server's sweep, not a log. */
const MAX_PENDING = 5;

interface SessionRow {
  id: string;
  /** Firebase uid the row was opened for; null for rows stored before it was kept. */
  uid: string | null;
}

interface PendingRow extends SessionRow {
  /** The reason to close it with. */
  reason: string;
}

interface Stored {
  current: SessionRow | null;
  pending: PendingRow[];
}

const EMPTY: Stored = { current: null, pending: [] };

const readStored = async (): Promise<Stored> => {
  let raw: string | null = null;
  try {
    raw = await AsyncStorage.getItem(STORAGE_KEY);
  } catch {
    return { ...EMPTY };
  }
  if (!raw) {
    return { ...EMPTY };
  }
  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // An older build stored the bare id.
    return { current: { id: raw, uid: null }, pending: [] };
  }
  if (parsed && Array.isArray(parsed.pending)) {
    const current = parsed.current && typeof parsed.current.id === 'string'
      ? { id: parsed.current.id, uid: typeof parsed.current.uid === 'string' ? parsed.current.uid : null }
      : null;
    const pending = parsed.pending
      .filter((row: any) => row && typeof row.id === 'string')
      .map((row: any) => ({
        id: row.id,
        uid: typeof row.uid === 'string' ? row.uid : null,
        reason: typeof row.reason === 'string' ? row.reason : 'logout',
      }));
    return { current, pending };
  }
  if (parsed && typeof parsed.id === 'string') {
    // The single-row shape: { id, uid, pendingEnd? }.
    const row = { id: parsed.id, uid: typeof parsed.uid === 'string' ? parsed.uid : null };
    return typeof parsed.pendingEnd === 'string'
      ? { current: null, pending: [{ ...row, reason: parsed.pendingEnd }] }
      : { current: row, pending: [] };
  }
  return { ...EMPTY };
};

const writeStored = async (stored: Stored): Promise<void> => {
  try {
    if (!stored.current && stored.pending.length === 0) {
      await AsyncStorage.removeItem(STORAGE_KEY);
    } else {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    }
  } catch {
    // best-effort
  }
};

/** The HTTP status the server answered with, if it answered. */
const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;

/** Adds a row to the pending list, the oldest dropped past the cap. */
const withPending = (pending: PendingRow[], row: PendingRow): PendingRow[] =>
  [...pending.filter((p) => p.id !== row.id), row].slice(-MAX_PENDING);

/**
 * Open a session on the server and persist its ID locally. Safe to call
 * after every successful login (including biometric). No-throw on failure.
 *
 * The rows this user left unclosed are closed first, with their reasons; one
 * that fails again stays pending. Other users' rows are left for them, or for
 * the server's stale-sessions sweep.
 */
export async function startSession(uid: string | null = null): Promise<void> {
  const stored = await readStored();
  if (uid) {
    const kept: PendingRow[] = [];
    for (const row of stored.pending) {
      if (row.uid !== uid) {
        kept.push(row);
        continue;
      }
      try {
        await v2EndSession(row.id, row.reason);
      } catch (err) {
        console.warn('[sessionTracker] closing a previous session failed:', err);
        if (statusOf(err) !== 404) {
          kept.push(row);
        }
      }
    }
    stored.pending = kept;
  }
  try {
    const payload = await collectDeviceInfo();
    const { session_id } = await v2StartSession(payload);
    stored.current = { id: session_id, uid };
  } catch (err) {
    // Nothing was opened — under "Login as user" the server refuses (403).
    // The open row stays: after a restart during an impersonation it is the
    // super-user's, still to be closed.
    console.warn('[sessionTracker] startSession failed:', err);
  }
  await writeStored(stored);
}

/**
 * Close the currently-open session, if any. Safe to call from logout —
 * no-throw. Closed, or unknown to the server (404), it is gone. Refused for
 * the token (401 expired, 403 not this user's) or unanswered, it goes on the
 * pending list, closed on its owner's next sign-in.
 */
export async function endSession(reason: string = 'logout'): Promise<void> {
  const stored = await readStored();
  const row = stored.current;
  if (!row) {
    return;
  }
  stored.current = null;
  try {
    await v2EndSession(row.id, reason);
  } catch (err) {
    console.warn('[sessionTracker] endSession failed:', err);
    if (statusOf(err) !== 404) {
      stored.pending = withPending(stored.pending, { ...row, reason });
    }
  }
  await writeStored(stored);
}

/**
 * Marks the open row to be closed on its owner's next sign-in, without asking
 * the server — for when no token can close it now: a session that expired
 * during "Login as user" holds the client's token, and the row is the
 * super-user's.
 */
export async function markSessionForEnd(reason: string): Promise<void> {
  const stored = await readStored();
  if (!stored.current) {
    return;
  }
  stored.pending = withPending(stored.pending, { ...stored.current, reason });
  stored.current = null;
  await writeStored(stored);
}
